/* Copyright (c) 2026 Xperi. All Rights Reserved. */

/**
 * featureStageOverrideStorage — DEBUG-ONLY persistence for **Stage Overrides**.
 *
 * A sibling of `featureOverrideStorage`, deliberately kept in its own
 * `localStorage` key rather than sharing one map: the two solve different
 * problems (an activation override short-circuits the decision; a stage
 * override only substitutes the lifecycle stage), they have different value
 * domains, and resetting one must never disturb the other.
 *
 *   featureActivation.overrides       { "LIVETV": true, "MYHUB": false }
 *   featureActivation.stageOverrides  { "LIVETV": "IN_TESTING" }
 *
 * Synchronous `localStorage`, because the Feature Activation read path
 * (`isFeatureEnabled`) is synchronous and cannot await IndexedDB.
 * `FeatureActivation` never touches this module directly — reads reach it
 * through `FeatureStageOverrideProvider`, writes through the Feature Override
 * ViewModel.
 *
 * Everything here is gated by `isDevToolsBuild()`: in a release build every
 * entry point is inert (reads return `null`/`{}`, writes are no-ops), so no
 * stage override can exist even if a `localStorage` entry was left behind by a
 * previous debug build on the same device.
 *
 * Two independent filters run on every read, so a hand-edited or stale entry
 * can never change behaviour in an unintended way:
 *   - the key must be a registered `Feature`;
 *   - the value must be an *overridable* stage (`DISABLED`, `IN_DEVELOPMENT`,
 *     `IN_TESTING`). `RELEASED` is rejected like any other invalid value — it
 *     is the only production-visible stage, and no debug setting may grant it.
 */

import {
  isOverridableFeatureStage,
  registeredFeatures
} from "../../model/featureActivation";
import type { Feature, FeatureStage } from "../../model/featureActivation";
import { Log } from "../../model/logging/Log";
import { isDevToolsBuild } from "../appVersionOverride";

const TAG: string = "featureStageOverrideStorage";

/** Namespaced key holding the developer/QE stage-override map. */
export const FEATURE_STAGE_OVERRIDE_STORAGE_KEY: string =
  "featureActivation.stageOverrides";

const KNOWN_FEATURES: ReadonlySet<string> = new Set<string>(registeredFeatures);

function hasBrowserStorage(): boolean {
  return (
    typeof window !== "undefined" && typeof window.localStorage !== "undefined"
  );
}

/**
 * The full stage-override map, restricted to registered features and
 * overridable stages. Returns an empty map in a release build, when storage is
 * unavailable, or when the stored value is missing or malformed — a corrupt
 * value is treated as absent rather than crashing the app.
 */
export function readFeatureStageOverrides(): Record<string, FeatureStage> {
  if (!isDevToolsBuild() || !hasBrowserStorage()) {
    return {};
  }
  try {
    const raw: string | null = window.localStorage.getItem(
      FEATURE_STAGE_OVERRIDE_STORAGE_KEY
    );
    if (raw === null) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return {};
    }
    const source = parsed as Record<string, unknown>;
    const result: Record<string, FeatureStage> = {};
    const keys: Array<string> = Object.keys(source);
    for (let i = 0; i < keys.length; i += 1) {
      const key: string = keys[i];
      const value: unknown = source[key];
      if (KNOWN_FEATURES.has(key) && isOverridableFeatureStage(value)) {
        result[key] = value;
      }
    }
    return result;
  } catch (error: unknown) {
    Log.w(
      TAG,
      "readFeatureStageOverrides() — unreadable stage-override store",
      error
    );
    return {};
  }
}

function writeFeatureStageOverrides(
  overrides: Record<string, FeatureStage>
): void {
  if (!isDevToolsBuild() || !hasBrowserStorage()) {
    return;
  }
  try {
    window.localStorage.setItem(
      FEATURE_STAGE_OVERRIDE_STORAGE_KEY,
      JSON.stringify(overrides)
    );
  } catch (error: unknown) {
    Log.w(
      TAG,
      "writeFeatureStageOverrides() — could not persist stage overrides",
      error
    );
  }
}

/** The stage override for one feature, or `null` when none is set. */
export function getFeatureStageOverride(feature: Feature): FeatureStage | null {
  const overrides: Record<string, FeatureStage> = readFeatureStageOverrides();
  if (Object.prototype.hasOwnProperty.call(overrides, feature)) {
    return overrides[feature];
  }
  return null;
}

/**
 * Evaluates a feature as if the registry declared `stage`. A non-overridable
 * stage (including `RELEASED`) is rejected and leaves the stored map untouched.
 */
export function setFeatureStageOverride(
  feature: Feature,
  stage: FeatureStage
): void {
  if (!isOverridableFeatureStage(stage)) {
    Log.w(TAG, "setFeatureStageOverride() — rejected stage " + String(stage));
    return;
  }
  const overrides: Record<string, FeatureStage> = readFeatureStageOverrides();
  overrides[feature] = stage;
  writeFeatureStageOverrides(overrides);
}

/** Removes a single stage override so the registry stage resumes (DEFAULT). */
export function clearFeatureStageOverride(feature: Feature): void {
  const overrides: Record<string, FeatureStage> = readFeatureStageOverrides();
  if (Object.prototype.hasOwnProperty.call(overrides, feature)) {
    delete overrides[feature];
    writeFeatureStageOverrides(overrides);
  }
}

/**
 * Clears every stage override (Reset All Stage Overrides). Activation overrides
 * live under a different key and are deliberately left untouched.
 */
export function clearAllFeatureStageOverrides(): void {
  if (!isDevToolsBuild() || !hasBrowserStorage()) {
    return;
  }
  try {
    window.localStorage.removeItem(FEATURE_STAGE_OVERRIDE_STORAGE_KEY);
  } catch (error: unknown) {
    Log.w(
      TAG,
      "clearAllFeatureStageOverrides() — could not clear stage overrides",
      error
    );
  }
}
